import * as T from "three";
import { addSky, breeze } from "./art-direction";
import { makeWorld, obstacles, cameraProxies } from "./world";
import { loadModels } from "./assets";
import { layout, BLOCK_INNER } from "./layout";
import { freshLap, parkCourse, stepLap } from "./park-lap";
import { character, crowd, PACKS, type PackId } from "./character";
import { traffic } from "./traffic";
import { readGamepad, GAMEPAD_LOOK_SPEED } from "./gamepad";
import { interactions, type Interactable } from "./interact";
import { drivable } from "./vehicle";
import { connect } from "./net";
import { BUILDING_STYLES, VEHICLE_STYLES, type BuildingStyle, type VehicleStyle } from "./styles";
import { clearCameraBoom } from "./camera";
import {
  CITY,
  WALK,
  terrainHeight,
  surfaceHeight,
  locations,
  road,
  moveWithCollisions,
  type LocationId,
} from "./world-data";
const $ = <E extends HTMLElement = HTMLElement>(id: string) =>
  document.getElementById(id)! as E;
const canvas = $<HTMLCanvasElement>("world");
let renderer: T.WebGLRenderer;
try {
  renderer = new T.WebGLRenderer({
    canvas,
    antialias: true,
    powerPreference: "default",
    failIfMajorPerformanceCaveat: false,
  });
} catch {
  $("loading").innerHTML =
    "<h2>This adventure needs WebGL 2.</h2><p>Please try a recent browser with hardware acceleration enabled.</p>";
  throw new Error("WebGL 2 unavailable");
}
const isTouch = matchMedia("(pointer:coarse)").matches;
// Which graphics chip the browser is using. Software renderers (graphics
// acceleration off or unavailable) run this game at only a few FPS.
const gpuName = (() => {
  try {
    const gl = renderer.getContext();
    const info = gl.getExtension("WEBGL_debug_renderer_info");
    return String(gl.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : gl.RENDERER));
  } catch {
    return "unknown";
  }
})();
const softwareGpu = /swiftshader|llvmpipe|software|basic render|microsoft basic/i.test(gpuName);
renderer.setPixelRatio(Math.min(devicePixelRatio, isTouch ? 1.25 : 1.65));
renderer.setSize(innerWidth, innerHeight);
renderer.shadowMap.enabled = !isTouch;
renderer.shadowMap.type = T.PCFSoftShadowMap;
renderer.outputColorSpace = T.SRGBColorSpace;
renderer.toneMapping = T.ACESFilmicToneMapping;
renderer.toneMappingExposure = 0.95;
const scene = new T.Scene();
scene.background = new T.Color("#b8cec5");
scene.fog = new T.FogExp2("#b8d7d5", 0.0028);
const camera = new T.PerspectiveCamera(58, innerWidth / innerHeight, 0.1, 600);
const hemi = new T.HemisphereLight("#c5e4ff", "#465333", 1.8);
scene.add(hemi);
addSky(scene);
const sun = new T.DirectionalLight("#fff0cf", 2.7);
sun.position.set(-50, 85, 35);
sun.castShadow = true;
sun.shadow.mapSize.set(2048, 2048);
// Shadows cover the whole valley on the welcome view, then follow the player
// over a smaller area once playing (fewer objects to draw into the shadow map).
const sunOffset = new T.Vector3(-50, 85, 35);
function shadowArea(half: number) {
  const c = sun.shadow.camera;
  c.left = c.bottom = -half;
  c.right = c.top = half;
  c.updateProjectionMatrix();
}
shadowArea(85);
sun.shadow.camera.near = 1;
sun.shadow.camera.far = 250;
sun.shadow.bias = -0.0005;
sun.shadow.normalBias = 0.07;
scene.add(sun);
sun.target.position.set(0, 0, -30);
scene.add(sun.target);
const world = makeWorld(scene),
  avatar = character();
scene.add(avatar.root);
avatar.root.position.set(locations.downtown.x, 0, locations.downtown.z);
// Pedestrians walking the pavements round every block.
const pedestrianLoops = [-CITY.L, 0, CITY.L].flatMap((cx) =>
  [-CITY.L, 0, CITY.L].map((cz) => ({ cx, cz, half: CITY.L / 2 - WALK })),
);
const pedestrians = crowd(scene, pedestrianLoops, isTouch ? 12 : 24);
const controlsLine = $("controls-line");
// Moving traffic (local only); cars give way to the player.
const cars = traffic(scene, isTouch ? 18 : 36);
// Walking, driving a car, or sitting on a bench.
let mode: "walk" | "drive" | "sit" = "walk";
// Keys already held when sitting must not stand you straight back up.
let sitHold = false;
let interactPressed = false;
const drive = drivable(scene);
const things = interactions();
world.vehicles.list.forEach((car, i) =>
  things.add({
    id: `car-${i}`,
    where: () => car,
    range: 3.4,
    label: () => "Drive this car",
    enabled: () => mode === "walk" && !!world.vehicles.asset(i),
    act: () => enterCar(i),
  }),
);
for (const bench of layout.filter((p) => p.model.endsWith("bench.gltf")))
  things.add({
    id: `bench-${bench.x.toFixed(1)}-${bench.z.toFixed(1)}`,
    where: () => bench,
    range: 1.9,
    label: () => "Sit down",
    enabled: () => mode === "walk",
    act: () => {
      mode = "sit";
      sitHold = true;
      cancelWalk();
      walkVX = walkVZ = 0;
      avatar.root.position.set(bench.x, 0, bench.z);
      avatar.root.rotation.y = bench.rot;
    },
  });
// Multiplayer: null when playing solo (server unreachable or not joined yet).
let net: Awaited<ReturnType<typeof connect>> | null = null;
// With other players around, the server decides who gets a car.
function enterCar(i: number) {
  if (!net?.online) return enterCarNow(i);
  net.take(i).then((ok) => (ok ? enterCarNow(i) : toast("Someone else is driving that car.")));
}
function enterCarNow(i: number) {
  const look = world.vehicles.asset(i);
  if (!look) return;
  const car = world.vehicles.list[i];
  world.vehicles.take(i);
  drive.enter(i, car.x, car.z, car.rot, look);
  mode = "drive";
  cancelWalk();
  walkVX = walkVZ = 0;
  avatar.root.position.set(car.x, 0, car.z);
  avatar.root.rotation.y = car.rot;
  distance = 9;
}
// Last thing you could use, kept briefly so a press still lands while running past it.
let remembered: Interactable | null = null;
let rememberUntil = -1;
let offer: Interactable | null = null;
function inReach(x: number, z: number) {
  const hit = things.focus(x, z);
  if (hit) {
    remembered = hit;
    rememberUntil = elapsed + 0.4;
    return hit;
  }
  const item = remembered;
  if (!item || elapsed > rememberUntil || (item.enabled && !item.enabled())) {
    remembered = null;
    return null;
  }
  const p = item.where();
  if (Math.hypot(p.x - x, p.z - z) > item.range + 1.5) {
    remembered = null;
    return null;
  }
  return item;
}
function exitCar() {
  const e = drive.exit();
  world.vehicles.park(e.index, e.x, e.z, e.heading);
  net?.park();
  // Step out on the driver's (right-hand) side, clear of anything solid.
  const rx = -Math.cos(e.heading), rz = Math.sin(e.heading);
  const out = moveWithCollisions({ x: e.x + rx * 2.4, z: e.z + rz * 2.4 }, 0, 0, [...obstacles, ...cars.obstacles]);
  avatar.root.position.set(out.x, surfaceHeight(out.x, out.z), out.z);
  avatar.root.rotation.y = e.heading;
  mode = "walk";
  distance = 6.5;
}
// Smoothed walking velocity, and a short grace period for jump presses.
let walkVX = 0,
  walkVZ = 0,
  jumpQueuedUntil = -1,
  jumpHeld = false,
  jumpEdge = false,
  padInteract = false,
  padDance = false;
const course = parkCourse(locations.park);
let lap = freshLap();
let foot = { x: locations.downtown.x, z: locations.downtown.z };
avatar.root.rotation.y = -Math.PI / 2;
let joined = false,
  scenic = false,
  reduced = matchMedia("(prefers-reduced-motion:reduce)").matches,
  yaw = Math.PI / 2,
  pitch = 0.3,
  distance = 6.5,
  vertical = 0,
  jumpY = 0,
  onGround = true,
  waveUntil = 0,
  danceUntil = 0,
  walkIndex = -1,
  elapsed = 0,
  toastTimer = 0,
  soundEnabled = false,
  snapCamera = true,
  context: AudioContext | null = null,
  soundGain: GainNode | null = null;
let profile = {
  name: "Explorer",
  pack: "city" as PackId,
  person: "Character_2_1_3",
  buildings: "quaternius" as BuildingStyle,
  vehicles: "kenney" as VehicleStyle,
  quality: isTouch ? "low" : "high",
  reduced,
};
try {
  const saved = JSON.parse(
    localStorage.getItem("mft-adventure-local-profile") || "null",
  );
  if (saved && typeof saved.name === "string")
    profile = { ...profile, ...saved };
  if (!(profile.pack in PACKS)) profile.pack = "city";
  if (!(profile.person in PACKS[profile.pack].characters)) profile.person = Object.keys(PACKS[profile.pack].characters)[0];
  if (!(profile.buildings in BUILDING_STYLES)) profile.buildings = "quaternius";
  if (!(profile.vehicles in VEHICLE_STYLES)) profile.vehicles = "kenney";
} catch {}
$<HTMLInputElement>("nickname").value = profile.name.slice(0, 20);
$<HTMLInputElement>("reduced").checked = !!profile.reduced;
reduced = !!profile.reduced;
function save() {
  try {
    localStorage.setItem(
      "mft-adventure-local-profile",
      JSON.stringify(profile),
    );
  } catch {}
}
function toast(s: string, ms = 3200) {
  $("toast").textContent = s;
  $("toast").classList.add("show");
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(
    () => $("toast").classList.remove("show"),
    ms,
  );
}
const keys = new Set<string>();
const stick = { x: 0, z: 0 };
function cancelWalk() {
  walkIndex = -1;
  $("route-banner").hidden = true;
}
function clearInput() {
  keys.clear();
  jumpEdge = false;
  jumpHeld = false;
  joyEnd();
  if (drag && canvas.hasPointerCapture(drag.id)) canvas.releasePointerCapture(drag.id);
  drag = null;
}
function modal(id: string) {
  if (document.pointerLockElement) document.exitPointerLock();
  clearInput();
  cancelWalk();
  $<HTMLDialogElement>(id).showModal();
}
function anyModal() {
  return !!document.querySelector("dialog[open]");
}
// Physical keys, so a focused button or link cannot swallow Space, and Shift
// still releases when the key comes up. Space is handled once per press in the
// frame loop (keyboard and gamepad share that edge).
function controlKey(e: KeyboardEvent): string | null {
  switch (e.code) {
    case "KeyW": return "w";
    case "KeyA": return "a";
    case "KeyS": return "s";
    case "KeyD": return "d";
    case "ArrowUp": return "arrowup";
    case "ArrowDown": return "arrowdown";
    case "ArrowLeft": return "arrowleft";
    case "ArrowRight": return "arrowright";
    case "Space": return " ";
    case "ShiftLeft":
    case "ShiftRight": return "shift";
    case "KeyF": return "f";
    case "KeyG": return "g";
    case "KeyE": return "e";
    case "KeyM": return "m";
    case "KeyT": return "t";
    case "Enter":
    case "NumpadEnter": return "enter";
    default: break;
  }
  // Some browsers omit `code` on a second key while another key is held, so
  // Space never arrives as "Space" during a walk. `key` still says what it is.
  switch (e.key) {
    case "w": case "W": return "w";
    case "a": case "A": return "a";
    case "s": case "S": return "s";
    case "d": case "D": return "d";
    case "ArrowUp": return "arrowup";
    case "ArrowDown": return "arrowdown";
    case "ArrowLeft": return "arrowleft";
    case "ArrowRight": return "arrowright";
    case " ": case "Spacebar": return " ";
    case "Shift": return "shift";
    case "f": case "F": return "f";
    case "g": case "G": return "g";
    case "e": case "E": return "e";
    case "m": case "M": return "m";
    case "t": case "T": return "t";
    case "Enter": return "enter";
    default: return e.keyCode === 32 ? " " : null;
  }
}
function typingField(target: EventTarget | null) {
  return target instanceof Element && !!target.closest("input,select,textarea");
}
addEventListener("keydown", (e) => {
  if (e.key === "Escape" && document.pointerLockElement) document.exitPointerLock();
  if (!joined || typingField(e.target) || anyModal()) return;
  const key = controlKey(e);
  if (!key) return;
  if (key === " " || key === "enter" || key.startsWith("arrow")) e.preventDefault();
  if (["w", "a", "s", "d", "arrowup", "arrowdown", "arrowleft", "arrowright"].includes(key)) cancelWalk();
  keys.add(key);
  // A new press, not the auto-repeat of a key that is already down.
  if (key === " " && !e.repeat) {
    jumpEdge = true;
    jumpHeld = false;
  }
  if (key === "f") interactPressed = true;
  if (key === "enter" || key === "t") openChat();
  if (key === "m") modal("journal");
  if (key === "e") waveUntil = elapsed + 2.5;
  if (key === "g" && mode === "walk") danceUntil = elapsed + 4;
}, true);
addEventListener("keyup", (e) => {
  const key = controlKey(e);
  if (key) keys.delete(key);
}, true);
addEventListener("blur", () => {
  clearInput();
  cancelWalk();
});
document.addEventListener("visibilitychange", () => {
  clearInput();
  if (document.hidden) cancelWalk();
});
let drag: { id: number; x: number; y: number } | null = null;
function turnCamera(dx: number, dy: number, scale: number) {
  yaw -= dx * scale;
  pitch = T.MathUtils.clamp(pitch + dy * scale * 0.8, 0.1, 1.05);
  if (scenic) scenic = false;
}
// Mouse look: a plain click on the world captures the mouse (pointer lock), then
// moving it turns the camera; Esc releases it. Dragging still works everywhere,
// and is the only option on touch screens.
const mouseLooking = () => document.pointerLockElement === canvas;
let pressedAt: { x: number; y: number } | null = null;
canvas.addEventListener("pointerdown", (e) => {
  if (!joined) return;
  canvas.focus();
  if (e.pointerType === "mouse") pressedAt = { x: e.clientX, y: e.clientY };
  if (mouseLooking()) return;
  drag = { id: e.pointerId, x: e.clientX, y: e.clientY };
  canvas.setPointerCapture(e.pointerId);
});
canvas.addEventListener("pointermove", (e) => {
  if (!drag || drag.id !== e.pointerId) return;
  turnCamera(e.clientX - drag.x, e.clientY - drag.y, 0.005);
  drag.x = e.clientX;
  drag.y = e.clientY;
});
document.addEventListener("mousemove", (e) => {
  if (mouseLooking() && !anyModal()) turnCamera(e.movementX, e.movementY, 0.0025);
});
canvas.addEventListener("pointerup", (e) => {
  drag = null;
  const click = pressedAt && Math.hypot(e.clientX - pressedAt.x, e.clientY - pressedAt.y) < 5;
  pressedAt = null;
  if (click && joined && !mouseLooking() && !isTouch && !anyModal()) {
    try {
      Promise.resolve(canvas.requestPointerLock()).catch(() => {});
    } catch {}
  }
});
canvas.addEventListener("pointercancel", () => (drag = null));
canvas.addEventListener("lostpointercapture", () => (drag = null));
canvas.addEventListener(
  "wheel",
  (e) => {
    e.preventDefault();
    distance = T.MathUtils.clamp(distance + e.deltaY * 0.012, 4, 18);
  },
  { passive: false },
);
const joystick = $("joystick"),
  knob = joystick.firstElementChild as HTMLElement;
let joyId: number | null = null;
function joy(e: PointerEvent) {
  const b = joystick.getBoundingClientRect(),
    dx = e.clientX - b.left - b.width / 2,
    dz = e.clientY - b.top - b.height / 2,
    d = Math.max(1, Math.hypot(dx, dz) / 35);
  stick.x = dx / d / 35;
  stick.z = dz / d / 35;
  if (Math.hypot(stick.x, stick.z) > 0.05) cancelWalk();
  knob.style.transform = `translate(${dx / d}px,${dz / d}px)`;
}
joystick.addEventListener("pointerdown", (e) => {
  if (joyId !== null) return;
  joyId = e.pointerId;
  joystick.setPointerCapture(e.pointerId);
  joy(e);
});
joystick.addEventListener("pointermove", (e) => {
  if (e.pointerId === joyId) joy(e);
});
function joyEnd() {
  const id = joyId;
  joyId = null;
  stick.x = stick.z = 0;
  knob.style.transform = "";
  if (id !== null && joystick.hasPointerCapture(id)) joystick.releasePointerCapture(id);
}
for (const type of ["pointerup", "pointercancel", "lostpointercapture"])
  joystick.addEventListener(type, (e) => {
    if ((e as PointerEvent).pointerId === joyId) joyEnd();
  });
$("jump-button").addEventListener("pointerdown", (e) => {
  e.preventDefault();
  jumpEdge = true;
  jumpHeld = false;
});
$("interact-button").addEventListener("click", () => (interactPressed = true));
$("join").addEventListener("submit", (e) => {
  e.preventDefault();
  profile.name =
    $<HTMLInputElement>("nickname").value.trim().slice(0, 20) || "Explorer";
  save();
  joined = true;
  snapCamera = true;
  document.body.classList.add("playing");
  $("welcome").hidden = true;
  $("hero-caption").hidden = true;
  $("hud").hidden = false;
  avatar.root.visible = true;
  if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  canvas.focus();
  requestAnimationFrame(() => canvas.focus());
  startMultiplayer();
  if (softwareGpu)
    toast(
      "Your browser isn't using your graphics card, so this will be slow. Turn on hardware acceleration in your browser settings.",
      12000,
    );
});
$("settings-open").onclick = () => modal("settings");
$("plaza-open").onclick = () => modal("plaza-info");
document.querySelectorAll<HTMLButtonElement>("[data-close]").forEach(
  (b) =>
    (b.onclick = () => {
      b.closest("dialog")!.close();
      canvas.focus();
    }),
);
function teleport(id: LocationId) {
  clearInput();
  cancelWalk();
  const l = locations[id];
  avatar.root.position.set(l.x, surfaceHeight(l.x, l.z), l.z);
  jumpY = vertical = 0;
  onGround = true;
  lap = { ...lap, phase: "idle", mark: 0 };
  foot.x = l.x;
  foot.z = l.z;
  scenic = false;
  yaw = id === "plaza" ? 0.65 : id === "downtown" ? Math.PI / 2 : 0.22;
  distance = id === "plaza" ? 10 : 6.5;
  pitch = id === "plaza" ? 0.42 : 0.3;
  snapCamera = true;
}
document.querySelectorAll<HTMLButtonElement>("[data-dest]").forEach(
  (b) =>
    (b.onclick = () => {
      teleport(b.dataset.dest as LocationId);
      $<HTMLDialogElement>("journal").close();
      canvas.focus();
    }),
);
$("reset").onclick = () => {
  teleport("downtown");
  $<HTMLDialogElement>("settings").close();
};
$("walk-trail").onclick = () => {
  $<HTMLDialogElement>("journal").close();
  teleport("downtown");
  walkIndex = 0;
  $("route-banner").hidden = false;
  scenic = true;
  canvas.focus();
};
$("route-cancel").onclick = cancelWalk;
// Dynamic resolution: the picture renders at `pixelScale` of the preset's
// resolution, lowered automatically when frames are slow and raised when there
// is headroom. Cheapest way to keep slow graphics chips responsive.
let pixelScale = 1,
  slowChecks = 0,
  qualityChosen = false;
function applyResolution() {
  const base = Math.min(devicePixelRatio, profile.quality === "low" ? 1 : isTouch ? 1.25 : 1.5);
  renderer.setPixelRatio(base * pixelScale);
  renderer.setSize(innerWidth, innerHeight);
}
function adaptResolution(averageFrame: number) {
  if (!joined || document.hidden) return;
  if (averageFrame > 0.028 && pixelScale > 0.5) {
    pixelScale = Math.max(0.5, pixelScale * 0.85);
    applyResolution();
  } else if (averageFrame < 0.014 && pixelScale < 1) {
    pixelScale = Math.min(1, pixelScale * 1.1);
    applyResolution();
  }
  // Still very slow at the lowest resolution: drop to Low graphics once, unless
  // the player picked a graphics setting themselves this visit.
  slowChecks = pixelScale <= 0.5 && averageFrame > 0.045 ? slowChecks + 1 : 0;
  if (slowChecks >= 4 && profile.quality === "high" && !qualityChosen) {
    $<HTMLSelectElement>("quality").value = "low";
    quality("low");
    pixelScale = 0.75;
    applyResolution();
    toast("Switched to Low graphics for smoother play (change in Settings)");
  }
}
function quality(v: string) {
  renderer.shadowMap.enabled = v === "high";
  world.setQuality(v);
  profile.quality = v;
  applyResolution();
  save();
}
$<HTMLSelectElement>("quality").value = profile.quality;
quality(profile.quality);
$<HTMLSelectElement>("quality").onchange = (e) => {
  qualityChosen = true;
  quality((e.target as HTMLSelectElement).value);
};
// Try-out settings: swap character pack, character, building style and
// vehicles between the free packs we have, to compare them in the real city.
const packSelect = $<HTMLSelectElement>("pack"),
  personSelect = $<HTMLSelectElement>("adventurer"),
  buildingSelect = $<HTMLSelectElement>("building-style"),
  vehicleSelect = $<HTMLSelectElement>("vehicle-style");
for (const [id, p] of Object.entries(PACKS)) packSelect.add(new Option(p.label, id));
for (const [id, b] of Object.entries(BUILDING_STYLES)) buildingSelect.add(new Option(b.label, id));
for (const [id, v] of Object.entries(VEHICLE_STYLES)) vehicleSelect.add(new Option(v.label, id));
function fillPeople() {
  personSelect.innerHTML = "";
  for (const [id, label] of Object.entries(PACKS[profile.pack].characters)) personSelect.add(new Option(label, id));
  personSelect.value = profile.person;
}
packSelect.value = profile.pack;
fillPeople();
buildingSelect.value = profile.buildings;
vehicleSelect.value = profile.vehicles;
// Chat: Enter or T opens a one-line box; messages show briefly, then fade.
const chatLog = $("chat-log"),
  chatInput = $<HTMLInputElement>("chat-input");
function addChat(from: string, text: string) {
  const line = document.createElement("div");
  if (from) {
    const who = document.createElement("b");
    who.textContent = `${from}: `;
    line.append(who);
  } else line.className = "note";
  line.append(text);
  chatLog.append(line);
  while (chatLog.children.length > 6) chatLog.firstElementChild!.remove();
  setTimeout(() => line.classList.add("fade"), 9000);
  setTimeout(() => line.remove(), 10500);
}
function openChat() {
  if (!net?.online) return toast("Chat needs the multiplayer server (playing solo).");
  if (document.pointerLockElement) document.exitPointerLock();
  clearInput();
  chatInput.hidden = false;
  chatInput.focus();
}
chatInput.addEventListener("keydown", (e) => {
  if (e.key === "Enter") {
    const text = chatInput.value.trim();
    if (text) net?.chat(text);
    chatInput.value = "";
  }
  if (e.key === "Enter" || e.key === "Escape") {
    chatInput.hidden = true;
    canvas.focus();
  }
  e.stopPropagation();
});
async function startMultiplayer() {
  if (net) return;
  try {
    net = await connect(
      scene,
      { name: profile.name, pack: profile.pack, person: profile.person },
      (i) => world.vehicles.asset(i),
      {
        chat: (from, text) => addChat(from, text),
        correct: (x, z) => {
          if (mode === "drive") Object.assign(drive.state, { x, z });
          else avatar.root.position.set(x, avatar.root.position.y, z);
        },
        carTaken: (index, byMe) => {
          if (!byMe) world.vehicles.take(index);
        },
        carParked: (index, x, z, rot) => {
          if (!(mode === "drive" && drive.state.index === index)) world.vehicles.park(index, x, z, rot);
        },
        count: (n) => {
          if (n > 0) $("online-count").textContent = `${n} in the city`;
        },
      },
    );
    // The server places new arrivals side by side; start from there.
    if (net.spawn && mode === "walk") avatar.root.position.set(net.spawn.x, 0, net.spawn.z);
    toast("You're online. Press Enter to chat.");
  } catch (error) {
    console.warn("Multiplayer unavailable:", error);
    net = null;
    $("online-count").textContent = "Solo · server offline";
  }
}
async function changeCharacter() {
  net?.look(profile.pack, profile.person);
  save();
  toast("Loading character…");
  try {
    await avatar.use(profile.pack, profile.person);
    toast(`Now playing as ${(PACKS[profile.pack].characters as Record<string, string>)[profile.person]}.`);
  } catch {
    toast("Couldn't load that character. Please check your connection.");
  }
}
packSelect.onchange = () => {
  profile.pack = packSelect.value as PackId;
  profile.person = Object.keys(PACKS[profile.pack].characters)[0];
  fillPeople();
  changeCharacter();
};
personSelect.onchange = () => {
  profile.person = personSelect.value;
  changeCharacter();
};
buildingSelect.onchange = async () => {
  profile.buildings = buildingSelect.value as BuildingStyle;
  save();
  toast("Rebuilding the city…");
  try {
    world.setBuildingStyle(profile.buildings, await loadModels(buildingModels(profile.buildings)));
    toast(`Buildings: ${BUILDING_STYLES[profile.buildings].label}`);
  } catch {
    toast("Couldn't load those buildings. Please check your connection.");
  }
};
vehicleSelect.onchange = async () => {
  profile.vehicles = vehicleSelect.value as VehicleStyle;
  save();
  try {
    const models = await loadModels([...VEHICLE_STYLES[profile.vehicles].models]);
    world.setVehicleStyle(profile.vehicles, models);
    cars.setStyle(profile.vehicles, models);
    toast(`Vehicles: ${VEHICLE_STYLES[profile.vehicles].label}`);
  } catch {
    toast("Couldn't load those vehicles. Please check your connection.");
  }
};
function buildingModels(style: BuildingStyle) {
  const own = BUILDING_STYLES[style].models;
  return own.length ? [...own] : [...new Set(layout.filter((p) => p.kind === "building").map((p) => p.model))];
}
$<HTMLInputElement>("reduced").onchange = (e) => {
  reduced = (e.target as HTMLInputElement).checked;
  profile.reduced = reduced;
  save();
};
$<HTMLInputElement>("metrics-toggle").onchange = (e) =>
  ($("metrics").hidden = !(e.target as HTMLInputElement).checked);
$("sound").onclick = async () => {
  if (!context) {
    context = new AudioContext();
    const buffer = context.createBuffer(
        1,
        context.sampleRate * 4,
        context.sampleRate,
      ),
      a = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < a.length; i++) {
      last = (last + Math.random() * 0.04 - 0.02) * 0.985;
      a[i] = last;
    }
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    const filter = context.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 850;
    soundGain = context.createGain();
    soundGain.gain.value = 0;
    source.connect(filter).connect(soundGain).connect(context.destination);
    source.start();
  }
  await context.resume();
  soundEnabled = !soundEnabled;
  soundGain!.gain.setTargetAtTime(
    soundEnabled ? 0.18 : 0,
    context.currentTime,
    0.5,
  );
  $("sound").setAttribute(
    "aria-label",
    soundEnabled ? "Mute ambient sound" : "Enable ambient sound",
  );
  $("sound").style.color = soundEnabled ? "#e4bd79" : "";
  $("sound").querySelector("span")!.textContent = soundEnabled ? "Sound on" : "Sound off";
  toast(soundEnabled ? "Ambient wind on" : "Ambient sound off");
};
addEventListener("resize", () => {
  camera.aspect = innerWidth / innerHeight;
  camera.updateProjectionMatrix();
  applyResolution();
});
let last = performance.now(),
  frames = 0,
  frameTime = 0,
  lastStats = 0;
const targetCamera = new T.Vector3(),
  targetLook = new T.Vector3(),
  look = new T.Vector3(0, 8, -30);
camera.position.set(150, 95, 150);
avatar.root.visible = false;
const cameraBlockers: T.Object3D[] = cameraProxies;
world.root.updateMatrixWorld(true);
const frameSamples: number[] = [];
const inspect = new URLSearchParams(location.search).has("inspect");
if (inspect) Object.defineProperty(window, "__embervale", { get: () => ({
  position: avatar.root.position.toArray(),
  camera: camera.position.toArray(),
  focus: look.toArray(),
  joined, scenic, walkIndex, jumpY, onGround,
  input: { keys: [...keys], stick: { ...stick }, dragging: !!drag },
  quality: profile.quality,
  soundEnabled,
  frames: [...frameSamples],
  render: { ...renderer.info.render },
  drawingBuffer: [canvas.width, canvas.height],
  pixelScale,
}) });
// CPU time spent per frame on game code and on preparing the draw, so the stats
// overlay can tell "slow code" apart from "slow graphics card".
let logicTime = 0,
  submitTime = 0;
function animate(now: number) {
  requestAnimationFrame(animate);
  const cpuStart = performance.now();
  const raw = (now - last) / 1000,
    // Allow up to 0.1 s per frame so slow devices still move at full speed
    // (a lower cap made the character move in slow motion below 20 FPS).
    dt = Math.min(raw, 0.1);
  last = now;
  if (document.hidden) return;
  if (inspect && raw > 0) {
    frameSamples.push(raw * 1000);
    if (frameSamples.length > 600) frameSamples.shift();
  }
  elapsed += dt;
  if (!reduced) breeze.value = elapsed;
  frames++;
  frameTime += raw;
  let speed = 0;
  const pad = readGamepad();
  if (joined && !anyModal()) {
    // Gamepad: right stick looks around; buttons jump / interact on press.
    if (pad.connected) {
      yaw -= pad.lookX * GAMEPAD_LOOK_SPEED * dt;
      pitch = T.MathUtils.clamp(pitch + pad.lookY * GAMEPAD_LOOK_SPEED * 0.6 * dt, 0.1, 1.05);
      if (pad.interact && !padInteract) interactPressed = true;
      if (pad.dance && !padDance && mode === "walk") danceUntil = elapsed + 4;
    }
    // A tap that went down and up between frames sets jumpEdge. Holding Space
    // or the gamepad button does not jump again. A new Space press clears
    // jumpHeld in the key handler, so a stuck button cannot eat the tap.
    const jumpDown = keys.has(" ") || pad.jump || jumpEdge;
    let jumpPressed = jumpEdge || (jumpDown && !jumpHeld);
    jumpEdge = false;
    jumpHeld = keys.has(" ") || pad.jump;
    padInteract = pad.interact;
    padDance = pad.dance;
    let ix =
      (keys.has("d") || keys.has("arrowright") ? 1 : 0) -
      (keys.has("a") || keys.has("arrowleft") ? 1 : 0) +
      stick.x + pad.moveX;
    let iz =
      (keys.has("s") || keys.has("arrowdown") ? 1 : 0) -
      (keys.has("w") || keys.has("arrowup") ? 1 : 0) +
      stick.z - pad.moveY;
    const input = Math.hypot(ix, iz);
    const pressed = interactPressed;
    interactPressed = false;
    offer = null;
    if (mode === "drive") {
      // Driving: W/S or triggers for throttle/brake, A/D or stick to steer.
      const throttle = T.MathUtils.clamp(-iz + pad.throttle - pad.brake, -1, 1);
      drive.update(dt, { throttle, steer: T.MathUtils.clamp(-ix, -1, 1) }, [...obstacles, ...cars.obstacles]);
      avatar.root.position.set(drive.state.x, 0, drive.state.z);
      // Camera swings round behind the car.
      const behind = drive.state.heading + Math.PI;
      yaw += (T.MathUtils.euclideanModulo(behind - yaw + Math.PI, Math.PI * 2) - Math.PI) * Math.min(1, dt * 3);
      pitch += (0.3 - pitch) * Math.min(1, dt * 2);
      if (pressed) exitCar();
    } else if (mode === "sit") {
      if (input < 0.2) sitHold = false;
      // F stands even if movement keys are still down. A direction stands you
      // only after those keys have been released, so sitting while walking sticks.
      if (pressed || (!sitHold && input > 0.3)) {
        mode = "walk";
        sitHold = false;
      }
    } else {
      let dx = 0,
        dz = 0;
      if (input > 0.05) {
        cancelWalk();
        const norm = Math.max(input, 1);
        ix /= norm;
        iz /= norm;
        speed = keys.has("shift") || pad.sprint ? 8 : 5;
        // Analogue sticks walk slower when only pushed a little.
        speed *= Math.min(1, input);
        dx = (ix * Math.cos(yaw) + iz * Math.sin(yaw)) * speed;
        dz = (-ix * Math.sin(yaw) + iz * Math.cos(yaw)) * speed;
      }
      // Ease into and out of moving rather than starting/stopping instantly.
      const ease = Math.min(1, dt * (input > 0.05 ? 14 : 18));
      walkVX += (dx - walkVX) * ease;
      walkVZ += (dz - walkVZ) * ease;
      dx = walkVX * dt;
      dz = walkVZ * dt;
      speed = Math.hypot(walkVX, walkVZ);
      if (walkIndex >= 0) {
        const goal = road[walkIndex],
          p = avatar.root.position,
          dist = Math.hypot(goal.x - p.x, goal.z - p.z);
        if (dist < 0.75) {
          walkIndex++;
          if (walkIndex >= road.length) cancelWalk();
        } else {
          speed = 6;
          dx = ((goal.x - p.x) / dist) * speed * dt;
          dz = ((goal.z - p.z) / dist) * speed * dt;
          yaw = Math.atan2(-dx, -dz);
        }
      }
      // Collide in short sub-steps so a long frame can't skip through thin obstacles.
      const pos = avatar.root.position,
        substeps = Math.max(1, Math.ceil(Math.hypot(dx, dz) / 0.2));
      let moved = { x: pos.x, z: pos.z };
      const solid = [...obstacles, ...cars.obstacles]; // buildings, props and moving cars
      for (let i = 0; i < substeps; i++)
        moved = moveWithCollisions(moved, dx / substeps, dz / substeps, solid);
      pos.x = moved.x;
      pos.z = moved.z;
      if (speed > 0.3) {
        const heading = Math.atan2(dx, dz);
        avatar.root.rotation.y +=
          T.MathUtils.euclideanModulo(
            heading - avatar.root.rotation.y + Math.PI,
            Math.PI * 2,
          ) - Math.PI;
      }
      vertical -= 18 * dt;
      jumpY += vertical * dt;
      if (jumpY <= 0) {
        jumpY = 0;
        vertical = 0;
        onGround = true;
        if (elapsed < jumpQueuedUntil) {
          jumpQueuedUntil = -1;
          jumpPressed = true;
        }
      }
      // After the ground clamp, so this frame cannot zero a jump that was
      // pressed while already walking. Speed is not part of the check.
      if (jumpPressed) {
        if (onGround) {
          const step = Math.max(dt, 1 / 120);
          vertical = 6.2;
          onGround = false;
          jumpY = vertical * step;
          vertical -= 18 * step;
          jumpQueuedUntil = -1;
        } else jumpQueuedUntil = elapsed + 0.2;
      }
      pos.y = surfaceHeight(pos.x, pos.z) + jumpY;
      // Resolve the press after moving, so running into range still counts.
      offer = inReach(pos.x, pos.z);
      if (pressed && offer) offer.act();
    }
    const here = { x: avatar.root.position.x, z: avatar.root.position.z };
    if (mode === "walk") lap = stepLap(lap, foot, here, course, elapsed);
    foot.x = here.x;
    foot.z = here.z;
  }
  avatar.update(
    elapsed,
    mode === "walk" ? speed : 0,
    jumpY,
    elapsed < waveUntil,
    mode === "sit",
    elapsed < danceUntil && mode === "walk",
  );
  const p = avatar.root.position;
  if (!joined) {
    const a = reduced ? 0 : Math.sin(elapsed * 0.045) * 0.045;
    // Slow aerial drift over the city on the welcome screen.
    const orbit = 0.78 + (reduced ? 0 : elapsed * 0.012);
    targetCamera.set(Math.cos(orbit) * 175, 92, Math.sin(orbit) * 175);
    targetLook.set(-30, 0, 0);
  } else if (scenic) {
    targetCamera.set(p.x + 25, p.y + 23, p.z + 28);
    targetLook.copy(p).add(new T.Vector3(0, 1, 0));
  } else {
    const d = distance;
    targetCamera.set(
      p.x + Math.sin(yaw) * d * Math.cos(pitch),
      p.y + 1.5 + Math.sin(pitch) * d,
      p.z + Math.cos(yaw) * d * Math.cos(pitch),
    );
    targetLook.copy(p).add(new T.Vector3(0, 1.3, 0));
  }
  targetCamera.y = Math.max(
    targetCamera.y,
    terrainHeight(targetCamera.x, targetCamera.z) + 1.5,
  );
  // Camera convergence follows wall time; the simulation's safety cap must not
  // turn an instant visit into a long flight on a slow rendering device.
  const blend = snapCamera || reduced ? 1 : 1 - Math.exp(-Math.min(raw, 1) * (joined ? 5 : 1));
  if (joined && !scenic) {
    // Follow view: turning is applied immediately (no drift behind the mouse);
    // only the point we follow is lightly smoothed to soften jumps and landings.
    look.lerp(targetLook, snapCamera || reduced ? 1 : 1 - Math.exp(-Math.min(raw, 1) * 18));
    camera.position.set(
      look.x + Math.sin(yaw) * distance * Math.cos(pitch),
      look.y + 0.2 + Math.sin(pitch) * distance,
      look.z + Math.cos(yaw) * distance * Math.cos(pitch),
    );
    camera.position.y = Math.max(camera.position.y, terrainHeight(camera.position.x, camera.position.z) + 1.5);
  } else {
    camera.position.lerp(targetCamera, blend);
    look.lerp(targetLook, blend);
  }
  snapCamera = false;
  if (joined && !scenic) clearCameraBoom(look, camera.position, cameraBlockers);
  // Hide the player when a wall pushes the camera right up to them, instead of
  // filling the screen with the inside of their head.
  avatar.root.visible = joined && mode !== "drive" && camera.position.distanceTo(look) > 1.1;
  camera.lookAt(look);
  if (joined) {
    // Snap to whole metres so shadows don't shimmer while walking.
    sun.target.position.set(Math.round(p.x), Math.round(p.y), Math.round(p.z));
    sun.position.copy(sun.target.position).add(sunOffset);
    if (sun.shadow.camera.right !== 50) shadowArea(50);
  }
  world.update(camera.position);
  pedestrians.update(dt, camera.position);
  cars.update(dt, avatar.root.position);
  if (net && joined) {
    const anim =
      mode === "drive" ? "drive" : mode === "sit" ? "sit" : jumpY > 0.05 ? "jump" : elapsed < danceUntil ? "dance"
      : speed > 6.5 ? "sprint" : speed > 3.5 ? "run" : speed > 0.2 ? "walk" : "idle";
    const pos = mode === "drive" ? { x: drive.state.x, z: drive.state.z } : avatar.root.position;
    net.send(now, { x: pos.x, z: pos.z, y: jumpY, rot: mode === "drive" ? drive.state.heading : avatar.root.rotation.y, anim });
    net.update(dt);
  }
  // One control line. The action you can take right now is part of that line.
  const near = mode === "walk" ? offer : null;
  const lookHint = isTouch ? "drag look" : "click look";
  let line = "";
  if (joined && !anyModal()) {
    if (mode === "drive") line = `W S drive · A D steer · ${lookHint} · F get out`;
    else if (mode === "sit") line = `F stand · WASD move · ${lookHint} · Space jump`;
    else if (elapsed < lap.flashUntil && lap.last != null && lap.phase === "idle") {
      const best = lap.best != null ? ` · best ${lap.best.toFixed(1)}s` : "";
      line = `Lap ${lap.last.toFixed(1)}s${best} · Space jump · Shift sprint`;
    } else if (lap.phase === "run") {
      line = `Lap ${(elapsed - lap.startedAt).toFixed(1)}s · mark ${lap.mark}/${course.marks.length} · Space jump · Shift sprint`;
    } else if (near) {
      const use = near.id.startsWith("car") ? "F drive" : "F sit";
      line = `${use} · WASD move · ${lookHint} · Shift sprint · Space jump · G dance`;
    } else if (Math.hypot(p.x - locations.park.x, p.z - locations.park.z) < BLOCK_INNER) {
      line = "Park lap · run between the crates · WASD move · Space jump · Shift sprint · G dance";
    } else line = `WASD move · ${lookHint} · Shift sprint · Space jump · F use · G dance`;
  }
  if (controlsLine.dataset.text !== line) {
    controlsLine.dataset.text = line;
    controlsLine.hidden = !line;
    controlsLine.textContent = line;
  }
  const submitStart = performance.now();
  renderer.render(scene, camera);
  logicTime += submitStart - cpuStart;
  submitTime += performance.now() - submitStart;
  if (now - lastStats > 750) {
    adaptResolution(frameTime / frames);
    const atPlaza = Math.hypot(p.x - locations.plaza.x, p.z - locations.plaza.z) < BLOCK_INNER;
    $("context-action").hidden = !atPlaza;
    $("metrics").textContent =
      `${Math.round(frames / frameTime)} FPS · ${((frameTime / frames) * 1000).toFixed(1)} ms/frame\n${renderer.info.render.calls} draw calls\n${Math.round(renderer.info.render.triangles / 1000)}k triangles\n${renderer.domElement.width} × ${renderer.domElement.height}\nPosition ${p.x.toFixed(1)}, ${p.y.toFixed(1)}, ${p.z.toFixed(1)}\nCPU per frame: game ${(logicTime / frames).toFixed(1)} ms · draw ${(submitTime / frames).toFixed(1)} ms\nGraphics: ${gpuName.slice(0, 60)}\nSolo preview · no network`;
    frames = 0;
    frameTime = 0;
    logicTime = submitTime = 0;
    lastStats = now;
  }
}
requestAnimationFrame(animate);
// Everything downloads before play starts: the loading bar tracks the
// character and the village/nature models; the terrain is generated locally.
(async () => {
  const bar = $("load-bar"), text = $("load-text");
  let character = 0, scenery = 0;
  const progress = () => {
    const fraction = character * 0.5 + scenery * 0.5;
    bar.style.width = `${Math.round(5 + fraction * 95)}%`;
    text.textContent = `Loading the city… ${Math.round(fraction * 100)}%`;
  };
  progress();
  try {
    // Scenery (minus buildings and cars, which depend on the chosen styles).
    const models = [
      ...new Set(layout.filter((p) => p.kind !== "building" && p.kind !== "car").map((p) => p.model)),
      ...buildingModels(profile.buildings),
      ...VEHICLE_STYLES[profile.vehicles].models,
    ];
    const [assets] = await Promise.all([
      loadModels(models, (f) => ((scenery = f), progress())),
      avatar.use(profile.pack, profile.person, (f) => ((character = f), progress())),
    ]);
    world.populate(assets);
    world.setBuildingStyle(profile.buildings, assets);
    world.setVehicleStyle(profile.vehicles, assets);
    cars.setStyle(profile.vehicles, assets);
    await pedestrians.ready;
  } catch {
    $("loading").innerHTML =
      "<h2>Couldn't load the adventure.</h2><p>Please check your connection and refresh.</p>";
    return;
  }
  bar.style.width = "100%";
  $("loading").style.opacity = "0";
  setTimeout(() => ($("loading").hidden = true), 900);
})();
